import React from 'react'
import { Plus, Search, Filter, Mic } from 'lucide-react'
import { usePodcasts, deletePodcast } from '../api'
import type { PodcastStatusValue } from '../types/podcast'
import { PodcastStatusEnum } from '../types/enums'
import { normalizeStatus } from '../lib/status-utils'
import Button from '../components/ui/button'
import PageHeader from '../components/page-header'
import Input from '../components/ui/input'
import ComboBox, { type ComboBoxOption } from '../components/ui/combobox'
import PodcastCard from '../components/podcast-card'
import ConfirmDialog from '../components/ui/confirm-dialog'
import { useKeyboardShortcuts } from '../hooks/use-keyboard-shortcuts'
import { KeyboardShortcutBadge } from '../components/ui/keyboard-shortcut-badge'
import { removeRecentEpisode } from '../lib/recent-episodes'
import { useNavigate } from 'react-router-dom'

const STATUS_OPTIONS: Array<ComboBoxOption<'All' | PodcastStatusValue>> = [
  { value: 'All', label: 'All' },
  { value: PodcastStatusEnum.Draft, label: normalizeStatus(PodcastStatusEnum.Draft) },
  { value: PodcastStatusEnum.Processing, label: normalizeStatus(PodcastStatusEnum.Processing) },
  { value: PodcastStatusEnum.Completed, label: normalizeStatus(PodcastStatusEnum.Completed) },
  { value: PodcastStatusEnum.Failed, label: normalizeStatus(PodcastStatusEnum.Failed) },
]


const PodcastsPage: React.FC = () => {
  const { data: podcasts = [], isLoading, isError, refetch } = usePodcasts()
  const [query, setQuery] = React.useState('')
  const [status, setStatus] = React.useState<'All' | PodcastStatusValue>('All')
  const [deleteDialog, setDeleteDialog] = React.useState<{ open: boolean; podcast: { id: string; title: string } | null }>({
    open: false,
    podcast: null,
  })
  const navigate = useNavigate()

  const normalizedQuery = query.trim().toLowerCase()

  const filtered = React.useMemo(() => {
    let items = podcasts
    if (status !== 'All') {
      // Filter by numeric status value directly
      items = items.filter(p => p.status === status)
    }
    if (normalizedQuery) items = items.filter(p => (p.title || 'Untitled podcast').toLowerCase().includes(normalizedQuery))
    return items
  }, [podcasts, status, normalizedQuery])

  const handleCreate = () => {
    navigate('/')
  }

  // Register keyboard shortcuts
  useKeyboardShortcuts({
    'n': handleCreate,
  })

  const handleDeleteClick = (podcast: { id: string; title: string }) => {
    setDeleteDialog({
      open: true,
      podcast,
    })
  }

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.podcast) return

    try {
      await deletePodcast(deleteDialog.podcast.id)
      removeRecentEpisode(deleteDialog.podcast.id)
      await refetch()
    } catch (error) {
      console.error('Failed to delete podcast:', error)
      throw error // Re-throw to keep dialog open on error
    }
  }

  const handleRefetch = async () => {
    await refetch()
  }

  return (
    <div className="space-y-8">
      <PageHeader
        title="Podcasts"
        subtitle={podcasts.length ? (
          <>
            <span className="font-medium text-foreground">{podcasts.length}</span> podcast{podcasts.length === 1 ? '' : 's'} created
          </>
        ) : 'Transform your ideas into engaging audio content'}
        actions={(
          <Button onClick={handleCreate} className="shadow-sm hover:shadow-md transition-shadow duration-200 flex items-center gap-2.5">
            <Plus className="h-4 w-4" aria-hidden="true" />
            Create podcast
            <KeyboardShortcutBadge shortcut="n" />
          </Button>
        )}
      />

      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
        <div className="flex-1 w-full">
          <Input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search podcasts..."
            aria-label="Search podcasts"
            leftIcon={<Search className="h-4 w-4" aria-hidden="true" />}
            className="bg-white/60 backdrop-blur-sm border-black/10 focus:border-brand-300 focus:bg-white transition-all duration-200"
          />
        </div>
        <div className="w-full sm:w-60">
          {/* Status combobox */}
          <ComboBox
            value={status}
            onChange={(v) => setStatus(v)}
            options={STATUS_OPTIONS}
            placeholder="All statuses"
            inputPlaceholder="Search status"
            renderButtonContent={(selected) => (
              <span className="inline-flex items-center gap-2.5 truncate">
                <Filter className="h-4 w-4 text-foreground/60" aria-hidden="true" />
                <span className="truncate font-medium">{selected ? selected.label : 'All statuses'}</span>
              </span>
            )}
          />
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" aria-busy="true">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="rounded-2xl border border-black/5 bg-white/60 backdrop-blur-sm p-6 animate-pulse">
              <div className="space-y-4">
                <div className="h-4 bg-foreground/10 rounded-lg w-3/4"></div>
                <div className="space-y-2">
                  <div className="h-3 bg-foreground/5 rounded w-full"></div>
                  <div className="h-3 bg-foreground/5 rounded w-2/3"></div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-6 bg-foreground/10 rounded-full w-16"></div>
                  <div className="h-3 bg-foreground/5 rounded w-20"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : isError ? (
        <div className="rounded-2xl border border-danger-200 bg-gradient-to-br from-danger-50 to-danger-100/50 p-8 backdrop-blur-sm">
          <div className="text-center max-w-md mx-auto">
            <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-danger-100 flex items-center justify-center">
              <Mic className="h-6 w-6 text-danger-600" />
            </div>
            <h3 className="text-lg font-semibold text-danger-900 mb-2">Unable to load podcasts</h3>
            <p className="text-sm text-danger-700 mb-6">Something went wrong while fetching your podcasts. Please try again.</p>
            <Button variant="secondary" onClick={() => refetch()} className="bg-white hover:bg-danger-50 border-danger-200">
              Try again
            </Button>
          </div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="relative">
          {/* Background decoration */}
          <div className="absolute inset-0 bg-gradient-to-br from-brand-50/30 via-transparent to-brand-100/20 rounded-3xl"></div>
          <div className="absolute top-8 right-8 w-32 h-32 bg-gradient-to-br from-brand-200/30 to-brand-300/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-8 left-8 w-24 h-24 bg-gradient-to-br from-indigo-200/30 to-purple-300/20 rounded-full blur-2xl"></div>

          <div className="relative rounded-3xl border border-black/5 bg-white/70 backdrop-blur-xl p-12 lg:p-16 text-center">
            <div className="max-w-lg mx-auto">
              <div className="space-y-4 mb-8">
                <h2 className="text-2xl lg:text-3xl font-bold text-foreground">
                  {podcasts.length ? 'No matching podcasts' : 'Your podcast studio awaits'}
                </h2>
                <p className="text-base lg:text-lg text-foreground/70 leading-relaxed">
                  {podcasts.length
                    ? 'Try adjusting your search terms or filters to find what you\'re looking for.'
                    : 'Transform articles, documents, or ideas into professional-quality podcasts with AI. Your first episode is just moments away.'}
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={handleCreate}
                  className="shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02] flex items-center gap-2.5"
                >
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  {podcasts.length ? 'Create new podcast' : 'Create your first podcast'}
                  <KeyboardShortcutBadge shortcut="n" />
                </Button>
                {podcasts.length > 0 && (
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setQuery('')
                      setStatus('All')
                    }}
                    className="hover:bg-white/60 border border-black/10 bg-white/30 backdrop-blur-sm"
                  >
                    Clear filters
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {filtered.map(p => (
            <PodcastCard
              key={p.id}
              podcast={p}
              onDelete={() => handleDeleteClick({ id: p.id, title: p.title || 'Untitled podcast' })}
              onRefetch={handleRefetch}
            />
          ))}
        </div>
      )}

      <ConfirmDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog({ open, podcast: open ? deleteDialog.podcast : null })}
        title="Delete podcast?"
        description={
          deleteDialog.podcast
            ? `Are you sure you want to delete "${deleteDialog.podcast.title}"? This action cannot be undone.`
            : undefined
        }
        confirmText="Delete"
        cancelText="Cancel"
        variant="destructive"
        onConfirm={handleDeleteConfirm}
      />
    </div>
  )
}

export default PodcastsPage
